import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { WHATSAPP_TEMPLATE_BLOCK_REASONS, WHATSAPP_TEMPLATE_PARAMETER_KINDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuCheck } from 'react-icons/lu';

import { TemplateButtonsRow } from '~/features/whatsapp/components/MessageStream/TemplateButtonsRow';
import { WhatsAppFormattedText } from '~/features/whatsapp/components/WhatsAppFormattedText';
import type { WhatsAppTemplate } from '~/features/whatsapp/hooks/useWhatsAppTemplates';
import {
    COMPUTED_VARIABLE_PREVIEW_EXAMPLES,
    isComputedVariablePreviewKey,
} from '~/features/whatsapp/utils/computedVariablePreviewExamples';
import {
    substituteTemplatePlaceholders,
    type TemplatePreviewSourceValues,
} from '~/features/whatsapp/utils/substituteTemplatePlaceholders';
import { templateNeedsApplicant, templateNeedsPartner } from '~/features/whatsapp/utils/templateNeedsApplicant';
import { cn } from '~/utils/cnTailwind';

type Props = {
    template: WhatsAppTemplate;
    expanded: boolean;
    onToggle: () => void;
    onUseTemplate: () => void;
    previewValues: TemplatePreviewSourceValues;
    hasLoanRequest: boolean;
    hasPartner: boolean;
};

// Custom values not yet known here, so custom tokens stay literal in the preview.
export const TemplatePickerListItem = ({
    expanded,
    hasLoanRequest,
    hasPartner,
    onToggle,
    onUseTemplate,
    previewValues,
    template,
}: Props) => {
    const { t } = useTranslation();
    const language = template.languages[0];

    const missingLoanRequest = templateNeedsApplicant({ language, template }) && !hasLoanRequest;
    const missingPartner = templateNeedsPartner({ template }) && !hasPartner;

    let blockReasonMessage: string | null = null;
    if (template.blockReasonCode) {
        blockReasonMessage =
            template.blockReasonCode === WHATSAPP_TEMPLATE_BLOCK_REASONS.UNKNOWN_VARIABLES
                ? t('WHATSAPP_CHAT.TEMPLATE_MODAL.BLOCK_REASON.UNKNOWN_VARIABLES', {
                      variables: (template.unknownVariableNames ?? []).join(', '),
                  })
                : t(`WHATSAPP_CHAT.TEMPLATE_MODAL.BLOCK_REASON.${template.blockReasonCode}`);
    }

    const previewValuesForSubstitution = {
        resolveComputedPreviewValue: (name: string) =>
            isComputedVariablePreviewKey(name) ? COMPUTED_VARIABLE_PREVIEW_EXAMPLES[name] : undefined,
        values: { ...previewValues, namedValues: {} },
    };
    const preview = language
        ? substituteTemplatePlaceholders({
              body: template.body[language] ?? '',
              parameters: template.parameters,
              ...previewValuesForSubstitution,
          })
        : '';
    const previewButtons = (language && template.buttons?.[language]) || [];
    const previewHeader =
        language && template.header?.[language]
            ? substituteTemplatePlaceholders({
                  body: template.header[language],
                  parameters: template.headerParameters ?? { kind: WHATSAPP_TEMPLATE_PARAMETER_KINDS.NONE },
                  ...previewValuesForSubstitution,
              })
            : '';

    return (
        <div className="border-b border-outline-light last:border-b-0">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={expanded}
                className={cn(
                    'flex w-full items-center justify-between px-3 py-2 text-left text-sm text-midnight hover:bg-surface-light',
                    !template.sendable && 'opacity-50',
                )}
            >
                <span className="truncate">{template.displayName}</span>
            </button>
            {expanded && (
                <div className="px-3 pb-3">
                    <div className="mb-2 overflow-hidden rounded-md bg-surface-light px-3 py-2 text-sm text-midnight">
                        {previewHeader && (
                            <div className="mb-1 whitespace-pre-wrap break-words font-semibold">
                                <WhatsAppFormattedText text={previewHeader} />
                            </div>
                        )}
                        <div className="whitespace-pre-wrap break-words">
                            <WhatsAppFormattedText text={preview} />
                        </div>
                        {previewButtons.length > 0 && (
                            <TemplateButtonsRow buttons={previewButtons} className="bg-grey-table-hover" />
                        )}
                    </div>
                    {blockReasonMessage && <p className="mb-2 text-xs text-alert-error">{blockReasonMessage}</p>}
                    {missingLoanRequest && (
                        <p className="mb-2 text-xs text-alert-error">
                            {t('WHATSAPP_CHAT.TEMPLATE_MODAL.MISSING_LOAN_REQUEST')}
                        </p>
                    )}
                    {missingPartner && (
                        <p className="mb-2 text-xs text-alert-error">
                            {t('WHATSAPP_CHAT.TEMPLATE_MODAL.MISSING_PARTNER')}
                        </p>
                    )}
                    <div className="flex justify-center">
                        <Button
                            type="button"
                            variant={ButtonVariant.PRIMARY_OUTLINE}
                            disabled={!template.sendable}
                            onClick={onUseTemplate}
                            iconLeft={<LuCheck className="size-4 shrink-0" />}
                        >
                            {t('WHATSAPP_CHAT.TEMPLATE_MODAL.USE_TEMPLATE')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
